import { Datakatalog } from '@/bootstrap/data';
import { EgenskapTillattVerdi, Egenskapstype } from '@/domain/vegobjekter/Egenskapstype';
import { Relasjon, Vegobjekttype } from '@/domain/vegobjekter/Vegobjekttype';
import { Operator } from '@/middleware/queries/Operator';
import {
    AdvancedFilterGroupNode,
    AdvancedFilterHash,
    AdvancedFilterHashGroup,
    AdvancedFilterHashNode,
    AdvancedFilterNode,
    AdvancedLogicalOperator,
    advancedFilterValueToHashValue,
    createAdvancedFilterEgenskapNode,
    createAdvancedFilterGroup,
    createAdvancedFilterOverlappNode,
    createAdvancedFilterRelasjonNode,
    createAdvancedFilterRoot,
    isAdvancedFilterGroupNode,
    isAdvancedFilterOverlappNode,
    isAdvancedFilterRelasjonNode,
} from './AdvancedFilterTypes';

export function normalizeAdvancedFilterRoot(
    root: AdvancedFilterGroupNode | null
): AdvancedFilterGroupNode | null {
    if (!root || root.children.length === 0) {
        return null;
    }
    return root;
}

export function appendAdvancedFilterNode(
    root: AdvancedFilterGroupNode | null,
    parentGroupId: string | null,
    node: AdvancedFilterNode
): AdvancedFilterGroupNode {
    if (!root) {
        return createAdvancedFilterRoot([node]);
    }

    if (!parentGroupId || root.id === parentGroupId) {
        return {
            ...root,
            children: [...root.children, node],
        };
    }

    return replaceAdvancedFilterNode(root, parentGroupId, currentNode => {
        if (!isAdvancedFilterGroupNode(currentNode)) {
            return currentNode;
        }

        return {
            ...currentNode,
            children: [...currentNode.children, node],
        };
    }) as AdvancedFilterGroupNode;
}

export function appendAdvancedFilterNodeToClause(
    root: AdvancedFilterGroupNode | null,
    clauseId: string,
    node: AdvancedFilterNode
): AdvancedFilterGroupNode | null {
    if (!root) {
        return root;
    }

    return replaceAdvancedFilterNode(root, clauseId, currentNode => {
        if (
            !isAdvancedFilterOverlappNode(currentNode) &&
            !isAdvancedFilterRelasjonNode(currentNode)
        ) {
            return currentNode;
        }

        const child = currentNode.child ?? createAdvancedFilterGroup();
        return {
            ...currentNode,
            child: {
                ...child,
                children: [...child.children, node],
            },
        };
    }) as AdvancedFilterGroupNode;
}

export function replaceAdvancedFilterNode(
    root: AdvancedFilterGroupNode | null,
    nodeId: string,
    replacer: (node: AdvancedFilterNode) => AdvancedFilterNode
): AdvancedFilterGroupNode | null {
    if (!root) {
        return null;
    }

    if (root.id === nodeId) {
        const replacedRoot = replacer(root);
        return isAdvancedFilterGroupNode(replacedRoot) ? replacedRoot : root;
    }

    return replaceNodeRecursive(root, nodeId, replacer) as AdvancedFilterGroupNode;
}

function replaceNodeRecursive(
    node: AdvancedFilterNode,
    nodeId: string,
    replacer: (node: AdvancedFilterNode) => AdvancedFilterNode
): AdvancedFilterNode {
    if (node.id === nodeId) {
        return replacer(node);
    }

    if (isAdvancedFilterGroupNode(node)) {
        return {
            ...node,
            children: node.children.map(child =>
                replaceNodeRecursive(child, nodeId, replacer)
            ),
        };
    }

    if (isAdvancedFilterOverlappNode(node) || isAdvancedFilterRelasjonNode(node)) {
        return {
            ...node,
            child: node.child
                ? replaceClauseChildRecursive(node.child, nodeId, replacer)
                : null,
        };
    }

    return node;
}

function replaceClauseChildRecursive(
    group: AdvancedFilterGroupNode,
    nodeId: string,
    replacer: (node: AdvancedFilterNode) => AdvancedFilterNode
): AdvancedFilterGroupNode {
    if (group.id === nodeId) {
        const replacedGroup = replacer(group);
        return isAdvancedFilterGroupNode(replacedGroup) ? replacedGroup : group;
    }

    return {
        ...group,
        children: group.children.map(child =>
            replaceNodeRecursive(child, nodeId, replacer)
        ),
    };
}

export function removeAdvancedFilterNode(
    root: AdvancedFilterGroupNode | null,
    nodeId: string
): AdvancedFilterGroupNode | null {
    if (!root) {
        return null;
    }

    if (root.id === nodeId) {
        return null;
    }

    return normalizeAdvancedFilterRoot(removeNodeFromGroup(root, nodeId));
}

function removeNodeFromGroup(
    group: AdvancedFilterGroupNode,
    nodeId: string
): AdvancedFilterGroupNode {
    const nextChildren = group.children
        .filter(child => child.id !== nodeId)
        .map(child => {
            if (isAdvancedFilterGroupNode(child)) {
                return removeNodeFromGroup(child, nodeId);
            }

            if (
                isAdvancedFilterOverlappNode(child) ||
                isAdvancedFilterRelasjonNode(child)
            ) {
                return {
                    ...child,
                    child: child.child
                        ? removeNodeFromClauseChild(child.child, nodeId)
                        : null,
                };
            }

            return child;
        });

    return {
        ...group,
        children: nextChildren,
    };
}

function removeNodeFromClauseChild(
    childGroup: AdvancedFilterGroupNode,
    nodeId: string
): AdvancedFilterGroupNode | null {
    if (childGroup.id === nodeId) {
        return null;
    }

    const nextChild = removeNodeFromGroup(childGroup, nodeId);
    if (nextChild.children.length === 0) {
        return null;
    }

    return nextChild;
}

export function updateAdvancedFilterGroupOperator(
    root: AdvancedFilterGroupNode | null,
    groupId: string,
    operator: AdvancedLogicalOperator
): AdvancedFilterGroupNode | null {
    return replaceAdvancedFilterNode(root, groupId, node => {
        if (!isAdvancedFilterGroupNode(node)) {
            return node;
        }
        return {
            ...node,
            operator,
        };
    });
}

export function updateAdvancedFilterGroupNegated(
    root: AdvancedFilterGroupNode | null,
    groupId: string,
    negated: boolean
): AdvancedFilterGroupNode | null {
    return replaceAdvancedFilterNode(root, groupId, node => {
        if (!isAdvancedFilterGroupNode(node)) {
            return node;
        }

        return {
            ...node,
            negated,
        };
    });
}

export function updateAdvancedFilterClauseNegated(
    root: AdvancedFilterGroupNode | null,
    clauseId: string,
    negated: boolean
): AdvancedFilterGroupNode | null {
    return replaceAdvancedFilterNode(root, clauseId, node => {
        if (!isAdvancedFilterOverlappNode(node) && !isAdvancedFilterRelasjonNode(node)) {
            return node;
        }

        return {
            ...node,
            negated,
        };
    });
}

export function findAdvancedFilterNode(
    root: AdvancedFilterGroupNode | null,
    nodeId: string
): AdvancedFilterNode | null {
    if (!root) {
        return null;
    }

    if (root.id === nodeId) {
        return root;
    }

    return findNodeRecursive(root, nodeId);
}

export type AdvancedFilterRelationDirection = 'forelder' | 'barn' | 'forelder/barn';

export interface AdvancedFilterRelationTargetOption {
    readonly targetTypeId: number;
    readonly targetTypeName: string;
    readonly direction: AdvancedFilterRelationDirection;
    readonly label: string;
}

export interface AdvancedFilterNodeContext {
    readonly contextType: Vegobjekttype | null;
    readonly contextRoot: AdvancedFilterGroupNode | null;
}

export function resolveAdvancedFilterRelationTargetOptions(
    root: AdvancedFilterGroupNode | null,
    relationNodeId: string,
    datakatalogVegobjekttyper: Vegobjekttype[],
    topLevelTypeId: number | null
): AdvancedFilterRelationTargetOption[] {
    const contextType = resolveAdvancedFilterContextTypeForNode(
        root,
        relationNodeId,
        datakatalogVegobjekttyper,
        topLevelTypeId
    );

    if (!contextType) {
        return [];
    }

    return buildRelationTargetOptions(contextType, datakatalogVegobjekttyper);
}

export function resolveAdvancedFilterContextTypeForNode(
    root: AdvancedFilterGroupNode | null,
    nodeId: string,
    datakatalogVegobjekttyper: Vegobjekttype[],
    topLevelTypeId: number | null
): Vegobjekttype | null {
    return resolveAdvancedFilterContextForNode(
        root,
        nodeId,
        datakatalogVegobjekttyper,
        topLevelTypeId
    ).contextType;
}

export function resolveAdvancedFilterContextForNode(
    root: AdvancedFilterGroupNode | null,
    nodeId: string,
    datakatalogVegobjekttyper: Vegobjekttype[],
    topLevelTypeId: number | null
): AdvancedFilterNodeContext {
    if (!root || !topLevelTypeId) {
        return { contextType: null, contextRoot: null };
    }

    const typeById = new Map(datakatalogVegobjekttyper.map(type => [type.id, type]));
    const context = findContextForNode(root, nodeId, topLevelTypeId, root);

    return {
        contextType: context ? (typeById.get(context.contextTypeId) ?? null) : null,
        contextRoot: context?.contextRoot ?? null,
    };
}

function findContextForNode(
    node: AdvancedFilterNode,
    nodeId: string,
    currentContextTypeId: number,
    currentContextRoot: AdvancedFilterGroupNode
): { contextTypeId: number; contextRoot: AdvancedFilterGroupNode } | null {
    if (node.id === nodeId) {
        return {
            contextTypeId: currentContextTypeId,
            contextRoot: currentContextRoot,
        };
    }

    if (isAdvancedFilterGroupNode(node)) {
        for (const child of node.children) {
            const result = findContextForNode(
                child,
                nodeId,
                currentContextTypeId,
                currentContextRoot
            );
            if (result !== null) {
                return result;
            }
        }

        return null;
    }

    if (
        (isAdvancedFilterOverlappNode(node) || isAdvancedFilterRelasjonNode(node)) &&
        node.child
    ) {
        return findContextForNode(
            node.child,
            nodeId,
            node.targetTypeId ?? currentContextTypeId,
            node.child
        );
    }

    return null;
}

function buildRelationTargetOptions(
    contextType: Vegobjekttype,
    datakatalogVegobjekttyper: Vegobjekttype[]
): AdvancedFilterRelationTargetOption[] {
    const typeById = new Map(datakatalogVegobjekttyper.map(type => [type.id, type]));
    const optionByTargetTypeId = new Map<number, AdvancedFilterRelationTargetOption>();

    addRelationOptions(
        optionByTargetTypeId,
        contextType.relasjonstyper?.foreldre ?? [],
        'forelder',
        typeById
    );
    addRelationOptions(
        optionByTargetTypeId,
        contextType.relasjonstyper?.barn ?? [],
        'barn',
        typeById
    );

    return [...optionByTargetTypeId.values()].sort((left, right) => {
        const directionCompare =
            relationDirectionSortRank(left.direction) -
            relationDirectionSortRank(right.direction);
        if (directionCompare !== 0) {
            return directionCompare;
        }

        return left.targetTypeName.localeCompare(right.targetTypeName, 'nb');
    });
}

function addRelationOptions(
    options: Map<number, AdvancedFilterRelationTargetOption>,
    relasjoner: Relasjon[],
    direction: 'forelder' | 'barn',
    typeById: Map<number, Vegobjekttype>
): void {
    for (const relasjon of relasjoner) {
        const targetTypeId = relasjon.innhold.type.id;
        const targetType = typeById.get(targetTypeId);
        const targetTypeName = targetType?.navn ?? relasjon.innhold.type.navn;
        const existing = options.get(targetTypeId);

        if (!existing) {
            options.set(targetTypeId, {
                targetTypeId,
                targetTypeName,
                direction,
                label: relationTargetLabel(targetTypeId, targetTypeName, direction),
            });
            continue;
        }

        const mergedDirection = mergeRelationDirections(existing.direction, direction);
        options.set(targetTypeId, {
            ...existing,
            direction: mergedDirection,
            label: relationTargetLabel(targetTypeId, targetTypeName, mergedDirection),
        });
    }
}

function mergeRelationDirections(
    currentDirection: AdvancedFilterRelationDirection,
    nextDirection: 'forelder' | 'barn'
): AdvancedFilterRelationDirection {
    if (currentDirection === nextDirection || currentDirection === 'forelder/barn') {
        return currentDirection;
    }

    return 'forelder/barn';
}

function relationTargetLabel(
    targetTypeId: number,
    targetTypeName: string,
    direction: AdvancedFilterRelationDirection
): string {
    return `${targetTypeId} - ${targetTypeName} (${direction})`;
}

function relationDirectionSortRank(direction: AdvancedFilterRelationDirection): number {
    switch (direction) {
        case 'forelder':
            return 0;
        case 'barn':
            return 1;
        case 'forelder/barn':
            return 2;
    }
}

function findNodeRecursive(
    node: AdvancedFilterNode,
    nodeId: string
): AdvancedFilterNode | null {
    if (node.id === nodeId) {
        return node;
    }

    if (isAdvancedFilterGroupNode(node)) {
        for (const child of node.children) {
            const match = findNodeRecursive(child, nodeId);
            if (match) {
                return match;
            }
        }
        return null;
    }

    if (
        (isAdvancedFilterOverlappNode(node) || isAdvancedFilterRelasjonNode(node)) &&
        node.child
    ) {
        return findNodeRecursive(node.child, nodeId);
    }

    return null;
}

export function excludeAdvancedFilterNode(
    root: AdvancedFilterGroupNode | null,
    nodeId: string
): AdvancedFilterGroupNode | null {
    if (!root) {
        return null;
    }

    if (root.id === nodeId) {
        return null;
    }

    const nextRoot = excludeNodeFromGroup(root, nodeId);
    return normalizeAdvancedFilterRoot(nextRoot);
}

function excludeNodeFromGroup(
    group: AdvancedFilterGroupNode,
    nodeId: string
): AdvancedFilterGroupNode {
    const children = group.children
        .map(child => excludeNodeRecursive(child, nodeId))
        .filter((child): child is AdvancedFilterNode => child !== null);

    return {
        ...group,
        children,
    };
}

function excludeNodeRecursive(
    node: AdvancedFilterNode,
    nodeId: string
): AdvancedFilterNode | null {
    if (node.id === nodeId) {
        return null;
    }

    if (isAdvancedFilterGroupNode(node)) {
        const nextGroup = excludeNodeFromGroup(node, nodeId);
        return nextGroup.children.length > 0 ? nextGroup : null;
    }

    if (isAdvancedFilterOverlappNode(node) || isAdvancedFilterRelasjonNode(node)) {
        if (!node.child) {
            return node;
        }

        const nextChild = excludeNodeFromGroup(node.child, nodeId);
        return {
            ...node,
            child: nextChild.children.length > 0 ? nextChild : null,
        };
    }

    return node;
}

export function advancedFilterToHash(
    root: AdvancedFilterGroupNode | null
): AdvancedFilterHash | null {
    if (!root) {
        return null;
    }

    return {
        version: 1,
        root: advancedFilterNodeToHash(root) as AdvancedFilterHashGroup,
    };
}

function advancedFilterNodeToHash(node: AdvancedFilterNode): AdvancedFilterHashNode {
    switch (node.kind) {
        case 'group':
            return {
                kind: 'group',
                negated: node.negated,
                operator: node.operator,
                children: node.children.map(advancedFilterNodeToHash),
            };
        case 'egenskap':
            if (node.egenskapstype === null || node.operator === null) {
                throw new Error(
                    'Cannot serialize incomplete egenskap node to hash: egenskapstype and operator must be set'
                );
            }
            return {
                kind: 'egenskap',
                type_id: node.egenskapstype.id,
                operator: Operator.asString(node.operator),
                verdi: advancedFilterValueToHashValue(node.verdi),
            };
        case 'overlapp':
            if (node.targetTypeId === null) {
                throw new Error(
                    'Cannot serialize incomplete overlapp node to hash: targetTypeId must be set'
                );
            }
            return {
                kind: 'overlapp',
                negated: node.negated,
                target_type_id: node.targetTypeId,
                child: node.child
                    ? (advancedFilterNodeToHash(node.child) as AdvancedFilterHashGroup)
                    : null,
            };
        case 'relasjon':
            if (node.targetTypeId === null) {
                throw new Error(
                    'Cannot serialize incomplete relasjon node to hash: targetTypeId must be set'
                );
            }
            return {
                kind: 'relasjon',
                negated: node.negated,
                target_type_id: node.targetTypeId,
                child: node.child
                    ? (advancedFilterNodeToHash(node.child) as AdvancedFilterHashGroup)
                    : null,
            };
    }
}

export function advancedFilterFromHash(
    hash: AdvancedFilterHash | null | undefined,
    datakatalog: Datakatalog,
    topLevelTypeId: number
): AdvancedFilterGroupNode | null {
    const normalizedHash = normalizeAdvancedFilterHash(hash);
    if (!normalizedHash?.root) {
        return null;
    }

    const hydratedRoot = advancedFilterNodeFromHash(
        normalizedHash.root,
        datakatalog,
        topLevelTypeId
    );
    return isAdvancedFilterGroupNode(hydratedRoot) ? hydratedRoot : null;
}

function normalizeAdvancedFilterHash(
    hash: AdvancedFilterHash | null | undefined
): AdvancedFilterHash | null {
    const normalized = normalizeAdvancedFilterHashValue(hash);
    if (!normalized || typeof normalized !== 'object') {
        return null;
    }

    const root = (normalized as { root?: unknown }).root;
    if (!root || typeof root !== 'object') {
        return null;
    }

    return normalized as AdvancedFilterHash;
}

function normalizeAdvancedFilterHashValue(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(item => normalizeAdvancedFilterHashValue(item));
    }

    if (!value || typeof value !== 'object') {
        return value;
    }

    const normalized: Record<string, unknown> = {};
    for (const [rawKey, rawValue] of Object.entries(value)) {
        assignAdvancedFilterHashValue(
            normalized,
            parseAdvancedFilterHashPath(rawKey),
            normalizeAdvancedFilterHashValue(rawValue)
        );
    }

    return normalized;
}

function parseAdvancedFilterHashPath(rawKey: string): string[] {
    const prefix = rawKey.split('[')[0];
    const bracketSegments = [...rawKey.matchAll(/\[([^\]]+)\]/g)].map(match => match[1]);
    const segments = prefix ? [prefix, ...bracketSegments] : bracketSegments;

    return segments.length > 0 ? segments : [rawKey];
}

function assignAdvancedFilterHashValue(
    target: Record<string, unknown> | unknown[],
    path: string[],
    value: unknown
): void {
    let current: Record<string, unknown> | unknown[] = target;

    for (let index = 0; index < path.length; index += 1) {
        const segment = path[index];
        const isLastSegment = index === path.length - 1;
        const nextSegment = path[index + 1];
        const createArray = nextSegment ? /^\d+$/.test(nextSegment) : false;

        if (Array.isArray(current)) {
            const arrayIndex = Number.parseInt(segment, 10);
            if (isLastSegment) {
                current[arrayIndex] = value;
                return;
            }

            const existing = current[arrayIndex];
            if (!existing || typeof existing !== 'object') {
                current[arrayIndex] = createArray ? [] : {};
            }

            current = current[arrayIndex] as Record<string, unknown> | unknown[];
            continue;
        }

        if (isLastSegment) {
            current[segment] = value;
            return;
        }

        const existing = current[segment];
        if (!existing || typeof existing !== 'object') {
            current[segment] = createArray ? [] : {};
        }

        current = current[segment] as Record<string, unknown> | unknown[];
    }
}

function advancedFilterNodeFromHash(
    node: AdvancedFilterHashNode,
    datakatalog: Datakatalog,
    contextTypeId: number
): AdvancedFilterNode | null {
    switch (node.kind) {
        case 'group':
            return createAdvancedFilterGroup(
                node.operator,
                node.children
                    .map(child =>
                        advancedFilterNodeFromHash(child, datakatalog, contextTypeId)
                    )
                    .filter((child): child is AdvancedFilterNode => child != null),
                String(node.negated) === 'true'
            );
        case 'egenskap': {
            const vegobjekttype = datakatalog.getTypeById(contextTypeId);
            const egenskapstype =
                vegobjekttype?.egenskapstypeById(Number(node.type_id)) ?? null;
            if (!egenskapstype) {
                return null;
            }

            const verdi = getAdvancedFilterValueFromHash(node.verdi, egenskapstype);
            return createAdvancedFilterEgenskapNode(
                egenskapstype,
                Operator.from(node.operator, verdi),
                verdi
            );
        }
        case 'overlapp': {
            const targetTypeId = Number(node.target_type_id);
            const child = node.child
                ? (advancedFilterNodeFromHash(
                      node.child,
                      datakatalog,
                      targetTypeId
                  ) as AdvancedFilterGroupNode)
                : null;
            return createAdvancedFilterOverlappNode(
                targetTypeId,
                child,
                String(node.negated) === 'true'
            );
        }
        case 'relasjon': {
            const targetTypeId = Number(node.target_type_id);
            const child = node.child
                ? (advancedFilterNodeFromHash(
                      node.child,
                      datakatalog,
                      targetTypeId
                  ) as AdvancedFilterGroupNode)
                : null;
            return createAdvancedFilterRelasjonNode(
                targetTypeId,
                child,
                String(node.negated) === 'true'
            );
        }
    }
}

function getAdvancedFilterValueFromHash(
    verdi: string | number | null,
    egenskapstype: Egenskapstype
): EgenskapTillattVerdi | number | string | null {
    if (verdi === null || verdi === undefined || verdi === '') {
        return null;
    }

    if (egenskapstype.erEnum()) {
        return (
            egenskapstype.verdiById(
                typeof verdi === 'number' ? verdi : Number.parseInt(verdi, 10)
            ) ?? null
        );
    }

    if (egenskapstype.erHeltall()) {
        return typeof verdi === 'number' ? verdi : Number.parseInt(verdi, 10);
    }

    if (egenskapstype.erFlyttall()) {
        return typeof verdi === 'number'
            ? verdi
            : Number.parseFloat(verdi.replace(',', '.'));
    }

    return verdi;
}

export function getAdvancedFilterExtraDepth(
    group: AdvancedFilterGroupNode | null,
    depth = 0
): number {
    if (!group) {
        return 0;
    }

    return group.children.reduce((maxDepth, child) => {
        return Math.max(maxDepth, getAdvancedFilterNodeExtraDepth(child, depth));
    }, depth);
}

function getAdvancedFilterNodeExtraDepth(
    node: AdvancedFilterNode,
    depth: number
): number {
    if (node.kind === 'group') {
        return getAdvancedFilterExtraDepth(node, depth + 1);
    }

    if ((node.kind === 'overlapp' || node.kind === 'relasjon') && node.child) {
        return getAdvancedFilterExtraDepth(node.child, depth + 1);
    }

    return depth;
}
